import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks";
import PropTypes from "prop-types";

import TwoColumnSection from "@/components/Organisms/TwoColumnSection";
import { FlexDiv } from "../../../components/Atoms";
import { WEBINAR_LEAD_ARGUMENTS } from "@/constants";
import RegisterForm from "@/components/Organisms/RegisterForm";
import SuccessModal from "@/components/Organisms/SuccessModal";
import ContactDetails from "@/components/Organisms/ContactDetails";
import FormWrapper from "@/components/Organisms/FormWrapper";
import { Typography } from "@/foundation/Typography/V2";
import theme from "@/foundation/Theme";
import { isMobile, noop, propTypeRef, timezone } from "@/utils";
import {
  resetNutritionCallbackValues,
  saveLead,
  postPackageSave as postNutritionCallbackData,
} from "@/redux/LandingPageReducer";
import { useRegisterForm } from "../../../hooks";

function BottomForm({
  data,
  fixedButtonRef,
  urlParams = "",
  slug,
  isWebinarRecordingsPage,
  isDoctorLandingPage = false,
}: any) {
  const {
    title,
    description,
    subtitle,
    number,
    email,
    services,
    namePlaceholder,
    phonePlaceholder,
    emailPlaceholder,
    buttonText,
    successImage,
    submissionTitle,
    submissionSubtitle,
    submissionExtraText,
    defaultSlug,
  } = data;

  const dispatch = useAppDispatch();
  const nutritionPageReducer = useAppSelector((state) => state.landingPage);
  const { countryCodeData } = useAppSelector((state) => state.dashboard);

  const {
    isLeadLoading: isLoading,
    leadError: error,
    leadSuccess: success,
  } = nutritionPageReducer;

  const urlsArguments = urlParams
    ? `${urlParams}${
        urlParams?.endsWith("&") ? "" : "&"
      }${WEBINAR_LEAD_ARGUMENTS}`
    : isWebinarRecordingsPage
    ? `?adset_name=recorded-webinar-referrals&${WEBINAR_LEAD_ARGUMENTS}`
    : `?${WEBINAR_LEAD_ARGUMENTS}`;

  const {
    showSuccessModal,
    isLoading: isThisPageLoading,
    handleBlur,
    handleChange,
    handleSelectChange,
    onSubmitCallback,
    onShowCallbackModal,
    onHideCallbackModal,
    inputs,
  } = useRegisterForm({
    name: "",
    countryCode: countryCodeData?.default?.code || "+91",
    mobile: "",
    goal: "",
    goalValue: "",
    service: "",
  });

  const { name, countryCode, mobile, goal, goalValue } = inputs;

  const onBlur = () =>
    dispatch(
      saveLead({
        data: {
          name: name,
          phone: mobile,
          phone_prefix: countryCode || "+91",
          package_slug: goal || defaultSlug,
          timezone: timezone,
          urls_arguments: urlsArguments,
          is_lead: true,
          ...(isDoctorLandingPage && {
            doctor_slug: slug,
            urls_arguments: urlParams,
          }),
        },
      })
    );

  const onSubmit = (state = true, callback = true, requested = "") => {
    let apiData = {
      mobile: mobile,
      name: name,
      package_slug: goal || slug,
      phone_prefix: countryCode ? countryCode : "+91",
      urls_arguments: urlsArguments,
      timezone: timezone,
    };
    if (callback === true) {
      apiData = {
        ...apiData,
        is_callback_request: callback,
      };
    } else if (requested === true) {
      apiData = {
        ...apiData,
        is_free_trial_requested: requested,
      };
    }
    dispatch(postNutritionCallbackData({ data: apiData })).then(() => {
      setServiceActive(0);
      onShowCallbackModal();
    });
  };

  const onHide = () => dispatch(resetNutritionCallbackValues());

  useEffect(() => {
    if (showSuccessModal === true) {
      setTimeout(() => onHideCallbackModal(onHide), 6000);
    }
  }, [showSuccessModal]);

  const onSubmitButtonClick = (e) => {
    e.preventDefault();
    onSubmitCallback(true, onSubmit);
  };

  // const options = dropdown;

  const [serviceActive, setServiceActive] = useState("");

  const [options, setOptions] = useState(
    services?.length > 0 ? services?.[serviceActive || 0]?.slugs : []
  );

  useEffect(() => {
    if (
      serviceActive === 0 &&
      services?.length > 0 &&
      services[serviceActive]?.value?.length > 0
    ) {
      handleSelectChange("service", services?.[serviceActive]?.value);
    }
  }, []);

  useEffect(() => {
    setOptions(
      services?.length > 0 ? services?.[serviceActive || 0]?.slugs : {}
    );
  }, [serviceActive]);

  useEffect(() => {
    let changedSlug = "";
    changedSlug =
      options?.length > 0
        ? options?.find((option) => option.value === goalValue)
        : "";
    handleSelectChange("goal", changedSlug?.slug);
  }, [options]);

  const leftSection = (
    <FlexDiv flexDirection="column">
      <FlexDiv flexDirection="column" flex="1">
        <Typography
          maxWidth="408px"
          mb="8px"
          color={theme.colorsV2.purple.shade_800}
          lineHeight=""
          variant="display2"
        >
          {title}
        </Typography>
        <Typography
          maxWidth="408px"
          mb={isMobile ? "24px" : "70px"}
          color={theme.colorsV2.rose.shade_400}
          variant="heading5"
        >
          {description}
        </Typography>
        <Typography
          mb={isMobile ? "24px" : "70px"}
          color={theme.colorsV2.gray.shade_700}
          variant="label1"
        >
          {subtitle}
        </Typography>
      </FlexDiv>
      <ContactDetails
        number={number}
        email={email}
        iconFill={theme.colorsV2.rose.shade_400}
        textColor={theme.colorsV2.gray.shade_700}
      />
    </FlexDiv>
  );

  const rightSection = (
    <FormWrapper>
      {!(success && showSuccessModal) && (
        <RegisterForm
          isLoading={isLoading && isThisPageLoading}
          showLabels={false}
          {...inputs}
          // Inputs
          name={name}
          countryCode={countryCode}
          mobile={mobile}
          //
          error={!success && error}
          onSubmitButtonClick={onSubmitButtonClick}
          marginBottom={isMobile ? "8px" : "16px"}
          handleBlur={(e) => handleBlur(e, onBlur)}
          handleChange={handleChange}
          handleSelectChange={handleSelectChange}
          // Goal
          showGoalsField={services?.length > 0}
          goalPlaceholder="Select condition"
          goalOptions={options}
          goal={goal ? options?.find((option) => option.slug === goal) : null}
          onGoalChange={(goal) => {
            handleSelectChange("goal", goal.slug);
            handleSelectChange("goalValue", goal.value);
          }}
          // Services
          showServicesField={services?.length > 0}
          serviceList={services}
          serviceLabel="What service are you looking for?"
          selectedServiceIndex={serviceActive}
          onServiceChange={(item, index) => {
            handleSelectChange("service", item.value);
            setServiceActive(index);
          }}
          // Name
          namePlaceholder={namePlaceholder}
          // Phone
          phonePlaceholder={phonePlaceholder}
          // Email
          emailPlaceholder={emailPlaceholder}
          // Button
          buttonText={buttonText}
          buttonPadding="15px 32px"
        />
      )}
      {success && showSuccessModal && (
        <SuccessModal
          successImage={successImage}
          submissionTitle={submissionTitle}
          submissionSubtitle={submissionSubtitle}
          submissionExtraText={submissionExtraText}
          isWebinar
        />
      )}
    </FormWrapper>
  );

  return (
    <TwoColumnSection
      reference={fixedButtonRef}
      leftSection={leftSection}
      rightSection={rightSection}
    />
  );
}

BottomForm.propTypes = {
  data: PropTypes.instanceOf(Object),
  fixedButtonRef: propTypeRef,
  urlParams: PropTypes.string,
  slug: PropTypes.string,
};

BottomForm.defaultProps = {
  data: {},
  fixedButtonRef: noop,
  urlParams: "",
  slug: "",
};

export default BottomForm;
